// Card payment integration seam (provider-agnostic).
//
// Card payments are OFF until the high-risk processor is approved. To go live:
//   1. Set CARD_PAYMENTS_ENABLED = true below.
//   2. Pick PAYMENT_MODE ('hosted' redirect or 'embedded' fields).
//   3. Fill the gateway TODOs in functions/src/index.ts (createCardPayment +
//      paymentWebhook) and set the PAYMENT_* env vars (see functions/.env.example).
//   4. Deploy:  firebase deploy --only functions,hosting
// Full checklist: PAYMENTS.md

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { PaymentMethod } from '../types';

export type { PaymentMethod };

// MASTER SWITCH — keep false until the processor is live. When false the
// checkout looks and behaves exactly as today (Interac e-Transfer only).
export const CARD_PAYMENTS_ENABLED = false;

// 'hosted'   = redirect to the processor's secure page (lowest PCI burden).
// 'embedded' = the gateway's secure card fields mount inside our checkout
//              (#card-payment-fields) via its client script (Accept.js / Collect.js).
export const PAYMENT_MODE: 'hosted' | 'embedded' = 'hosted';

export interface StartCardPaymentParams {
  orderNumber: string;
  amount: number;        // total to charge
  currency?: string;     // default CAD
  email?: string;
}

export interface CardPaymentResult {
  success: boolean;
  redirectUrl?: string;  // hosted mode: send the browser here
  clientToken?: string;  // embedded mode: hand to the gateway client script
  transactionId?: string;
  error?: string;
}

// Calls the provider-agnostic Cloud Function. Returns a "not configured" error
// until the gateway is wired up server-side and the env vars are set.
export const startCardPayment = async (
  params: StartCardPaymentParams
): Promise<CardPaymentResult> => {
  try {
    const fn = httpsCallable<StartCardPaymentParams, CardPaymentResult>(functions, 'createCardPayment');
    const res = await fn({ currency: 'CAD', ...params });
    return res.data;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Card payment could not be started.' };
  }
};
